import { useState, useEffect } from 'react';
import './ApolloModel.css';
import { Modal, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../auth';
import { ApolloForm, ApolloFormValues } from './ApolloForm';
import { ApolloList, ApolloData } from './ApolloList';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import { UserModel, PolicyData } from '../../auth/core/_models';

type Props = {
  show: boolean;
  handleClose: () => void;
  currentUser?: UserModel;
};

// Updated to use new multi-service payment architecture endpoints
const WEBHOOK_URL = 'https://etl.futurisk.in/webhook/futurisk-create-apollo-booking';
const LOAD_REQUESTS_URL = 'https://etl.futurisk.in/webhook/futurisk-get-apollo-bookings';

const ApolloModel: React.FC<Props> = ({ show, handleClose, currentUser: propsCurrentUser }) => {
  const auth = useAuth();
  const currentUser = propsCurrentUser || auth.currentUser;
  const [requests, setRequests] = useState<ApolloData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [redirectingUrl, setRedirectingUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<Array<{ name: string; relation: string; gender: string; dob: string }>>([]);
  const [loadingFamily, setLoadingFamily] = useState(false);

  // Load requests when modal opens
  useEffect(() => {
    let mounted = true;
    const loadRequests = async () => {
      if (!show) {
        // Reset state when modal closes
        setRequests([]);
        setShowForm(false);
        setIsSubmitted(false);
        setSubmissionError(null);
        setRedirectingUrl(null);
        setFamilyMembers([]);
        return;
      }
      
      setLoading(true);
      setShowForm(false);
      setIsSubmitted(false);
      setSubmissionError(null);
      setRedirectingUrl(null);
      setRequests([]);
      
      // Load family members data once at modal level
      if (familyMembers.length === 0) {
        setLoadingFamily(true);
        const email = currentUser?.Email;
        const corporateCode = currentUser?.Policies?.Entities?.[0]?.CrmDbName;
        
        try {
          const response = await fetch('https://etl.futurisk.in/webhook/getFamilyDetails', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              Email: email,
              CorporateCode: corporateCode,
            }),
          });
          if (response.ok) {
            const data: PolicyData = await response.json();
            const members = data?.policysummary.flatMap(summary => 
              summary.employeedtl.map(emp => ({
                name: emp.Name,
                relation: emp.Relation,
                gender: emp.Gender,
                dob: emp.DOBtxt
              }))
            ) || [];
            setFamilyMembers(members);
          }
        } catch (err) {
          console.error('Unable to load family members', err);
        } finally {
          setLoadingFamily(false);
        }
      }
      
      try {
        const payload = {
          CRMClientId: currentUser?.CRMClientId,
          EmployeeId: currentUser?.EmployeeId,
        };

        const resp = await fetch(LOAD_REQUESTS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!mounted) return;

        if (resp.ok) {
          const data = await resp.json();
          // Handle various response structures
          let requestList: ApolloData[];
          
          if (data.success && data.bookings) {
            // New n8n workflow format: {success, bookings: {...}}
            if (Array.isArray(data.bookings)) {
              // Extract .json property from each item if it exists (n8n array format)
              requestList = data.bookings.map((item: any) => item.json || item);
            } else {
              requestList = [data.bookings];
            }
          } else if (Array.isArray(data)) {
            // Direct array format - extract .json if exists
            requestList = data.map((item: any) => item.json || item);
          } else if (data && typeof data === 'object' && data.id) {
            // Single booking object (lowercase id)
            requestList = [data];
          } else if (data && typeof data === 'object' && data.ID) {
            // Single booking object (uppercase ID - legacy)
            requestList = [data];
          } else {
            requestList = [];
          }
          
          setRequests(requestList);
          
          // If no requests, show form directly
          if (requestList.length === 0) {
            setShowForm(true);
          } else {
            setShowForm(false);
          }
        } else {
          // On error, show form
          setRequests([]);
          setShowForm(true);
        }
      } catch (err) {
        if (mounted) {
          // On error, show form
          setRequests([]);
          setShowForm(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRequests();
    return () => { mounted = false; };
  }, [show, auth.currentUser]);

  const handleFormSubmit = async (values: ApolloFormValues) => {
    setSubmissionError(null);
    setCreating(true);
    try {
      const payload = {
        Name: values.Name,
        emailId: values.emailId,
        phoneNumber: values.phoneNumber,
        bookingDate: values.bookingDate,
        bookingTime: values.bookingTime,
        state: values.state,
        city: values.city,
        clinicId: values.clinicId,
        clinicName: values.clinicName,
        clinicAddress: values.clinicAddress,
        clinicPin: values.clinicPin,
        relation: values.relation,
        gender: values.gender,
        dob: values.dob,
        Fee: values.appointmentFee, // Match n8n workflow field name
        gst: values.gst,
        totalfee: values.totalFee, // Match n8n workflow field name (lowercase)
        CRMClientId: auth.currentUser?.CRMClientId,
        EmployeeId: auth.currentUser?.EmployeeId,
      };

      const resp = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Server error: ${resp.status} ${text}`);
      }

      const data = await resp.json();

      let redirectUrl: string | undefined;
      try {
        if (Array.isArray(data) && data.length > 0 && data[0].body?.responseData?.url) {
          redirectUrl = data[0].body.responseData.url;
        } else if (data?.body?.responseData?.url) {
          redirectUrl = data.body.responseData.url;
        } else if (data?.responseData?.url) {
          redirectUrl = data.responseData.url;
        }
      } catch (e) {
        // ignore parsing errors
      }

      setIsSubmitted(true);

      if (redirectUrl) {
        setRedirectingUrl(redirectUrl);
        try { window.open(redirectUrl, '_blank'); } catch (e) {}
        setTimeout(() => handleClose(), 800);
      } else {
        // No redirect URL - reload requests to show the new entry
        setTimeout(async () => {
          setIsSubmitted(false);
          setShowForm(false);
          setLoading(true);
          // Reload requests
          try {
            const loadPayload = {
              CRMClientId: auth.currentUser?.CRMClientId,
              EmployeeId: auth.currentUser?.EmployeeId,
            };
            const loadResp = await fetch(LOAD_REQUESTS_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(loadPayload),
            });
            if (loadResp.ok) {
              const loadData = await loadResp.json();
              let reloadedRequests: ApolloData[];
              
              if (loadData.success && loadData.bookings) {
                // New n8n workflow format
                if (Array.isArray(loadData.bookings)) {
                  // Extract .json property from each item if it exists
                  reloadedRequests = loadData.bookings.map((item: any) => item.json || item);
                } else {
                  reloadedRequests = [loadData.bookings];
                }
              } else if (Array.isArray(loadData)) {
                reloadedRequests = loadData.map((item: any) => item.json || item);
              } else if (loadData && typeof loadData === 'object' && (loadData.id || loadData.ID)) {
                reloadedRequests = [loadData];
              } else {
                reloadedRequests = [];
              }
              
              setRequests(reloadedRequests);
            }
          } catch (e) {
            // ignore reload errors
          } finally {
            setLoading(false);
          }
        }, 1500);
      }
    } catch (err: any) {
      setSubmissionError(err?.message || 'Submission failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      className='modal fade'
      id='kt_modal_apollo'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      aria-hidden='true'
      dialogClassName='modal-xl'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Apollo Health Checkup Booking</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-3x' />
          </div>

        </div>
        <div className='modal-body'>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading requests...</div>
            </div>
          ) : redirectingUrl ? (
            <div className="text-center">
              <div className="mb-3">Redirecting you...</div>
              <div>If nothing happens, <a href={redirectingUrl} target="_blank" rel="noreferrer">click here</a>.</div>
            </div>
          ) : isSubmitted ? (
            <div className="alert alert-success" role="alert">Booking submitted successfully!</div>
          ) : (showForm || requests.length === 0) ? (
            <Row>
              <Col xs={12} md={7}>
                <div className="apollo-test-ad p-4 d-flex flex-column" style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  color: 'white',
                  height: 'auto'
                }}>
                  <div className="text-center mb-4">
                    <h3 className="fw-bold mb-2" style={{ fontSize: '1.8rem', color: '#ffffff', fontWeight: '700' }}>
                      Comprehensive Health Checkup
                    </h3>
                    <div className="d-inline-block px-4 py-2 mb-3" style={{
                      background: 'rgba(76, 175, 80, 0.3)',
                      borderRadius: '25px',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.5)'
                    }}>
                      <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>FREE for Self</span>
                    </div>
                  </div>

                  <div className="row g-3">
                    {[
                      { icon: '🔬', name: 'X-Ray' },
                      { icon: '💉', name: 'Diabetes Profile' },
                      { icon: '🦋', name: 'Thyroid Profile' },
                      { icon: '🫘', name: 'Kidney Profile' },
                      { icon: '❤️', name: 'Lipid Profile' },
                      { icon: '🩸', name: 'CBC' },
                      { icon: '💓', name: 'ECG' },
                      { icon: '🏃', name: 'TMT' },
                      { icon: '🧪', name: 'Urine Microscopy' },
                      { icon: '🫀', name: 'Liver Function Test' },
                      { icon: '🫁', name: 'PFT' },
                      { icon: '⚖️', name: 'BMI' },
                      { icon: '👁️', name: 'Basic Vision Test' },
                      { icon: '🦷', name: 'Basic Dental Test' }
                    ].map((test, idx) => (
                      <div key={idx} className="col-6">
                        <div className="d-flex align-items-center p-2" style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '8px',
                          backdropFilter: 'blur(5px)',
                          transition: 'all 0.3s ease',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                          e.currentTarget.style.transform = 'translateX(5px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}>
                          <span style={{ fontSize: '1.3rem', marginRight: '10px' }}>{test.icon}</span>
                          <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#333' }}>{test.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 text-center">
                    <div className="d-inline-block px-4 py-3 mb-2" style={{
                      background: 'rgba(255, 255, 255, 0.25)',
                      borderRadius: '12px',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255, 255, 255, 0.4)'
                    }}>
                      <div style={{ fontSize: '0.95rem', marginBottom: '8px', opacity: 0.95 }}>
                        Bookings for dependents available at ₹2,500 + GST each.
                      </div>
                      
                    </div>
                  </div>
                </div>
              </Col>
              <Col xs={12} md={5}>
                <ApolloForm 
                  submissionError={submissionError}
                  creating={creating}
                  onSubmit={handleFormSubmit}
                  initialName={currentUser?.Name || ''}
                  initialEmail={currentUser?.Email! || ''}
                  initialPhone={currentUser?.PhoneNumber! || ''}
                  currentUser={currentUser}
                  familyMembers={familyMembers}
                  loadingFamily={loadingFamily}
                />
              </Col>
            </Row>
          ) : (
            <ApolloList 
                requests={requests}
                onAddRequest={() => setShowForm(true)}
              />
          )}
        </div>
        <div className='modal-footer'>
          * terms and conditions apply
        </div>
      </div>
    </Modal>
  );
};

export { ApolloModel };
